'use client';

import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useTransition,
} from 'react';
import { noop } from 'lodash';
import { usePathname, useRouter } from 'next/navigation';
import { isServer } from '@frameio/utils/config';
import { useCurrentPageType } from '../../routing';
import { getLink } from '../../routing/getLink';
import {
  getLinkForWorkspaces,
  isWorkspacePageType,
} from '../../routing/pages/workspaces';
import type { PageType } from '../../routing/types';
import { useActiveAccountId } from '../ActiveEntitiesContext';
import { IsolatedActiveAccountIdContextProvider } from '../ActiveEntitiesContext/ActiveAccountIdContext';
import { useIsIntegrationClient } from '../ClientNameContext';

type setForcedAccountId = (accountId: string) => void;
type SetForcedAccountIdContextValue = setForcedAccountId;

export const SetForcedAccountIdContext =
  createContext<SetForcedAccountIdContextValue>(noop);

const pageTypesWhereAccountIdCannotBeDerived: PageType[] = [
  'workspaces',
  'finder',
] as const;

export const useForceAccountId = () => {
  const [__isPending, startAccountChangeTransition] = useTransition();
  const activeAccountId = useActiveAccountId();
  const pageType = useCurrentPageType();
  const forceAccountId = useContext(SetForcedAccountIdContext);
  const router = useRouter();
  const pathname = usePathname();
  // the integrations panel has no root route, so workspaces links must use
  // the explicit /workspaces path or the account param is lost
  const isIntegrationClient = useIsIntegrationClient();

  /**
   * For pages where the account ID cannot be derived from the path, we need to
   * use window.history to establish two different states, one for the current
   * account ID and one for the account ID we're about to force
   */
  const windowHistoryStrategy = useCallback(
    (accountId: string) => {
      startAccountChangeTransition(() => {
        /**
         * Establish the current account in history state so that browser navigation
         * (e.g. the back button) will cause the account to update properly
         */
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete('a');
        window.history.replaceState(
          { accountId: activeAccountId },
          '',
          currentUrl.toString()
        );
        /**
         * Update the context value so that downstream account id contexts are
         * overridden.
         */
        forceAccountId(accountId);

        /**
         * Push the new account id to history state to differentiate it from the
         * previous state, which was usually the same page type, but a different
         * account ID. Again, so the browser back button works as expected.
         */
        window.history.pushState(
          { accountId },
          '',
          pageType === 'workspaces'
            ? getLinkForWorkspaces({ page: 'workspaces' }, isIntegrationClient)
            : getLink({ page: 'finder' })
        );
      });
    },
    [activeAccountId, forceAccountId, isIntegrationClient, pageType]
  );

  /**
   * For pages where the account ID can already be derived, use router as normal
   */
  const routerStrategy = useCallback(
    (accountId: string) => {
      startAccountChangeTransition(() => {
        router.push(
          getLinkForWorkspaces(
            {
              page: 'workspaces',
              accountId,
            },
            isIntegrationClient
          )
        );
      });
    },
    [isIntegrationClient, router]
  );

  const isPageTypeWhereAccountIdCannotBeDerived =
    pageTypesWhereAccountIdCannotBeDerived.includes(pageType);

  // @note (find-2381): do not use the history strategy when on individual workspace
  // pages (meaning `/workspaces/:id`) as that will break the root page since we do
  // not make a round-trip to the server.
  const isSingleWorkspacePage = isWorkspacePageType(pathname);

  return isPageTypeWhereAccountIdCannotBeDerived && !isSingleWorkspacePage
    ? windowHistoryStrategy
    : routerStrategy;
};

export const ForcedAccountIdFromHistoryState = ({
  children,
}: PropsWithChildren) => {
  const activeAccountId = useActiveAccountId();
  const [forcedAccountId, setForcedAccountId] = useState<string>();

  useEffect(function setupPopstateListener() {
    if (isServer) return noop;
    const onPopstate = (event: PopStateEvent) => {
      /** After a browser navigation, we've encountered a state that requires
       * forcing the account id. For example, when switching accounts on the
       * workspaces page, then using the browser back button
       **/
      if (event.state?.accountId) {
        setForcedAccountId(event.state.accountId);
      }
    };
    window.addEventListener('popstate', onPopstate);
    return () => {
      window.removeEventListener('popstate', onPopstate);
    };
  }, []);

  // Ensure that an account ID coming in from a parent context will clear any local forced id
  if (forcedAccountId && activeAccountId) {
    setForcedAccountId(undefined);
  }

  const accountId = activeAccountId || forcedAccountId;

  return (
    <SetForcedAccountIdContext.Provider value={setForcedAccountId}>
      <IsolatedActiveAccountIdContextProvider accountId={accountId}>
        {children}
      </IsolatedActiveAccountIdContextProvider>
    </SetForcedAccountIdContext.Provider>
  );
};
